import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import DynamicYieldAPI from 'api/DynamicYield';
import { CLICK } from 'api/DynamicYield/constants';
import Log from 'utils/Log';
import Rudderstack from 'utils/Rudderstack';
import { cn } from 'utils/Tailwind';

import { MINI_NAV_POSITIONS } from './constants';

const MiniNavigation = ({
  className,
  heading,
  isExperimentLoading,
  experimentAnalyticsData = {},
  links,
  position,
}) => {
  const { decisionId, variationId } = experimentAnalyticsData;

  const handleClick = useCallback(
    async ({ text, url, event }) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (decisionId) {
        try {
          const clickData = {
            type: CLICK,
            decisionId,
            variations: variationId ? [variationId] : [],
          };

          await DynamicYieldAPI.reportClick([clickData]);
        } catch (error) {
          Log.error(error, `Failed to send DY Engagement Click`);
        }
      }

      Rudderstack.navigationDelayForIphoneAndSafari({
        analyticsFn: () => {
          Rudderstack.miniNavigationClicked({
            linkCTAName: text,
            linkDestination: url,
          });
        },
        urlToNavigate: url,
      });

      return true;
    },
    [decisionId, variationId]
  );

  if (isExperimentLoading) return null;

  return (
    <div
      className={cn('MiniNavigation bg-white', className, {
        'flex overflow-x-auto scroll-smooth [scrollbar-width:none] md:justify-center [&::-webkit-scrollbar]:hidden':
          position === MINI_NAV_POSITIONS.TOP,
        'absolute bottom-10 z-10 hidden p-10 lg:block': position !== MINI_NAV_POSITIONS.TOP,
        'left-10': position === MINI_NAV_POSITIONS.BOTTOM_LEFT,
        'right-10': position === MINI_NAV_POSITIONS.BOTTOM_RIGHT,
        'lg:hidden': position === MINI_NAV_POSITIONS.HIDE,
      })}
    >
      {heading && position !== MINI_NAV_POSITIONS.TOP && (
        <p
          data-testid="heading"
          className="mb-4 hidden font-bold uppercase tracking-widest lg:block"
        >
          {heading}
        </p>
      )}

      <nav
        aria-label="Popular Category Navigation"
        className={cn('flex text-sm tracking-wider', {
          'gap-6 p-4 md:items-center': position === MINI_NAV_POSITIONS.TOP,
          'flex-col gap-2': position !== MINI_NAV_POSITIONS.TOP,
        })}
      >
        {links.map(({ text, url }) => (
          <a
            aria-label={text}
            className="group relative w-max whitespace-pre max-md:font-medium"
            href={url}
            key={text}
            onClick={event => handleClick({ text, url, event })}
          >
            <span>{text}</span>
            <span className="absolute bottom-0 left-1/2 h-px w-0 bg-black transition-all group-hover:md:w-3/6" />
            <span className="absolute bottom-0 right-1/2 h-px w-0 bg-black transition-all group-hover:md:w-3/6" />
          </a>
        ))}
      </nav>
    </div>
  );
};

MiniNavigation.propTypes = {
  className: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  heading: PropTypes.string,
  position: PropTypes.oneOf(Object.values(MINI_NAV_POSITIONS)),
  isExperimentLoading: PropTypes.bool,
  experimentAnalyticsData: PropTypes.shape({
    decisionId: PropTypes.string,
    variationId: PropTypes.number,
  }),
};

MiniNavigation.defaultProps = {
  className: '',
  links: [],
  heading: '',
  position: MINI_NAV_POSITIONS.BOTTOM_LEFT,
};

export default MiniNavigation;
